import type {
  DocumentVersion,
  EditorConfig,
  TeamSpace,
} from "@prismicio/editor-fields"
import { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import type { StaticCustomType } from "@prismicio/types-internal/lib/customtypes"
import { createContext } from "react"

import type { DocumentVersionStatus } from "../app/services/core"
import { type ExistingDocumentData, type NewDocumentData } from "./documentData"
import type { DocumentTitle } from "./useDocumentTitle"
import { type DocumentDraft } from "./useDraft"

export interface BaseDocumentContextValue {
  config: EditorConfig
  customType: StaticCustomType
  teamSpace: TeamSpace
  draft: DocumentDraft
  sectionNames: string[]
  sectionName: string
  edited: boolean
  status: DocumentVersionStatus
  versions: DocumentVersion[]
  locale: string
  content: DocumentT
  groupLangId?: string
  documentTitle: DocumentTitle
  edit: (newDocument: DocumentT) => void
}

export interface ExistingDocumentContextValue extends BaseDocumentContextValue {
  type: "existing"
  documentId: string
  data: ExistingDocumentData
  version: DocumentVersion
  title: string
  releaseId?: never
}

export interface NewDocumentContextValue extends BaseDocumentContextValue {
  type: "new"
  data: NewDocumentData
  releaseId?: string
  title?: string
  version?: never
  documentId?: never
}

export type DocumentContextValue =
  | ExistingDocumentContextValue
  | NewDocumentContextValue

/**
 * DocumentContext needs to be created in a separate file from DocumentProvider
 * because of a vite HMR bug: https://github.com/vitejs/vite/issues/3301#issuecomment-1192661323
 */
export const DocumentContext = createContext<DocumentContextValue | null>(null)
